/**
 * Mobile Apps Control — per-version block gate (iter-299o, Jun 2026)
 *
 * Every released version (registered by the platform agents via
 * POST /api/app/mobile-versions/register, or seen from a device check-in)
 * is listed with its own Block / Unblock button. Blocking is immediate —
 * the next check-in from that version receives `blocked: true` plus the
 * app's general bilingual update message (AR for Arabic apps, EN for
 * English apps) and the platform-specific store link.
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Smartphone, Loader2, Save, Apple, Bot, Users, Briefcase,
  RefreshCw, ExternalLink, AlertTriangle, Ban, ShieldCheck, Server, Radio, UserCog,
} from 'lucide-react';

import DashboardLayout from '../components/DashboardLayout';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { getAuthHeaders } from '../utils/authHeaders';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_COPY = {
  title_ar:  '✨ تحديث جديد في انتظارك!',
  body_ar:   'أهلاً بك! لقد أضفنا مزايا جديدة وتحسينات أمنية هامة لتستمتع بتجربة أكثر سلاسة وأماناً.\n\nنرجو منك تحديث التطبيق الآن لتكتشف كل ما هو جديد وتستفيد من أحدث الخصائص. شكراً لوقتك وثقتك بنا! 💙',
  button_ar: 'التحديث الآن',
  title_en:  '✨ A new update awaits you!',
  body_en:   "Welcome! We've added new features and important security improvements to bring you a smoother, safer experience.\n\nPlease update the app now to explore what's new and enjoy the latest features. Thank you for your time and trust! 💙",
  button_en: 'Update Now',
};

const APP_ICON = { cgm_business: Briefcase, cgc: Users };

const SOURCE_META = {
  platform: { ar: 'من المنصة', en: 'platform', Icon: Server,  cls: 'bg-sky-500/15 text-sky-300' },
  admin:    { ar: 'يدوي',      en: 'admin',    Icon: UserCog, cls: 'bg-amber-500/15 text-amber-300' },
  device:   { ar: 'من جهاز',   en: 'device',   Icon: Radio,   cls: 'bg-white/5 text-white/60' },
};

const ACTION_LABEL = {
  version_blocked:    { ar: 'حجب إصدار',      en: 'Version blocked',    cls: 'bg-red-500/15 text-red-300' },
  version_unblocked:  { ar: 'رفع حجب إصدار',  en: 'Version unblocked',  cls: 'bg-emerald-500/15 text-emerald-300' },
  version_registered: { ar: 'تسجيل إصدار',    en: 'Version registered', cls: 'bg-sky-500/15 text-sky-300' },
  versions_replaced:  { ar: 'مزامنة القائمة (المنصة)', en: 'List synced (platform)', cls: 'bg-sky-500/15 text-sky-300' },
  version_unregistered: { ar: 'إزالة إصدار (المنصة)', en: 'Version removed (platform)', cls: 'bg-white/5 text-white/60' },
  message_updated:    { ar: 'تعديل الرسالة',  en: 'Message updated',    cls: 'bg-amber-500/15 text-amber-300' },
  store_link_edited:  { ar: 'تعديل روابط المتجر', en: 'Store links edited', cls: 'bg-white/5 text-white/60' },
  block_created:      { ar: 'حجب (قديم)',     en: 'Block (legacy)',     cls: 'bg-red-500/10 text-red-300/70' },
  block_cleared:      { ar: 'رفع حجب (قديم)', en: 'Cleared (legacy)',   cls: 'bg-emerald-500/10 text-emerald-300/70' },
};

const buildDateTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const tz = { timeZone: 'Asia/Dubai' };
  const date = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', ...tz });
  const yy = String(new Date(d.toLocaleString('en-US', tz)).getFullYear()).slice(-2);
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', ...tz });
  return `${date} '${yy}, ${time}`;
};

export default function MobileAppsControl() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const t = (a, e) => (isAr ? a : e);

  const [apps, setApps]           = useState([]);
  const [activeApp, setActiveApp] = useState(null);
  const [activePlat, setActivePlat] = useState('ios');
  const [versions, setVersions]   = useState([]);
  const [audit, setAudit]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [busyVersion, setBusyVersion] = useState(null);
  const [savingMsg, setSavingMsg]   = useState(false);
  const [savingLinks, setSavingLinks] = useState(false);
  const [previewLocale, setPreviewLocale] = useState('ar');

  const [copy, setCopy]     = useState(DEFAULT_COPY);
  const [storeIos, setStoreIos]         = useState('');
  const [storeAndroid, setStoreAndroid] = useState('');

  const loadApps = useCallback(async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/admin/mobile-apps`, { headers: getAuthHeaders() });
      const list = r.data?.data || [];
      setApps(list);
      setActiveApp((cur) => cur || list[0]?.id || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || (isAr ? 'فشل تحميل التطبيقات' : 'Failed to load apps'));
    }
  }, [isAr]);

  const loadVersions = useCallback(async () => {
    if (!activeApp) return;
    setRefreshing(true);
    try {
      const r = await axios.get(
        `${BACKEND_URL}/api/admin/mobile-apps/${activeApp}/versions/${activePlat}`,
        { headers: getAuthHeaders() },
      );
      setVersions(r.data?.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || (isAr ? 'فشل تحميل الإصدارات' : 'Failed to load versions'));
    } finally {
      setRefreshing(false);
    }
  }, [activeApp, activePlat, isAr]);

  const loadAudit = useCallback(async () => {
    if (!activeApp) return;
    try {
      const r = await axios.get(`${BACKEND_URL}/api/admin/mobile-apps/${activeApp}/audit`, { headers: getAuthHeaders() });
      setAudit(r.data?.data || []);
    } catch { /* best-effort */ }
  }, [activeApp]);

  useEffect(() => {
    (async () => { setLoading(true); await loadApps(); setLoading(false); })();
  }, [loadApps]);

  useEffect(() => { loadVersions(); loadAudit(); }, [loadVersions, loadAudit]);

  useEffect(() => {
    const app = apps.find((a) => a.id === activeApp);
    if (!app) return;
    setStoreIos(app.ios_store_url || '');
    setStoreAndroid(app.android_store_url || '');
    setCopy({ ...DEFAULT_COPY, ...(app.update_message || {}) });
  }, [activeApp, apps]);

  const activeAppMeta = apps.find((a) => a.id === activeApp);
  const blockedCount = versions.filter((v) => v.blocked).length;

  const rowKey = (v) => `${v.version}${v.build_number ? `@${v.build_number}` : ''}`;

  const toggleBlock = async (v) => {
    const key = rowKey(v);
    setBusyVersion(key);
    const action = v.blocked ? 'unblock' : 'block';
    const label = v.build_number ? `v${v.version} · ${v.build_number}` : `v${v.version}`;
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/mobile-apps/${activeApp}/versions/${activePlat}/${encodeURIComponent(v.version)}/${action}`,
        {},
        { headers: getAuthHeaders(), params: v.build_number ? { build: v.build_number } : {} },
      );
      toast.success(
        v.blocked
          ? t(`تم رفع الحجب عن ${label}`, `${label} unblocked`)
          : t(`تم حجب ${label} — رسالة التحديث تظهر الآن لمستخدميه`,
              `${label} blocked — the update message is now shown to its users`),
      );
      await Promise.all([loadVersions(), loadAudit(), loadApps()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('فشلت العملية', 'Action failed'));
    } finally {
      setBusyVersion(null);
    }
  };

  const saveMessage = async () => {
    setSavingMsg(true);
    try {
      await axios.put(`${BACKEND_URL}/api/admin/mobile-apps/${activeApp}/message`, copy, { headers: getAuthHeaders() });
      toast.success(t('تم حفظ رسالة التحديث', 'Update message saved'));
      await Promise.all([loadApps(), loadAudit()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('فشل الحفظ', 'Save failed'));
    } finally {
      setSavingMsg(false);
    }
  };

  const saveLinks = async () => {
    setSavingLinks(true);
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/mobile-apps/${activeApp}/store-links`,
        { ios_store_url: storeIos, android_store_url: storeAndroid },
        { headers: getAuthHeaders() },
      );
      toast.success(t('تم حفظ روابط المتجر', 'Store links saved'));
      await Promise.all([loadApps(), loadAudit()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('فشل الحفظ', 'Save failed'));
    } finally {
      setSavingLinks(false);
    }
  };

  const breadcrumb = [
    { labelAr: 'التحكم والمعلومات للمنصات', labelEn: 'Platform Control & Info', path: '/dashboard?view=platform-control' },
    { labelAr: 'التحكم بتطبيقات الهاتف', labelEn: 'Mobile Apps Control', path: null },
  ];

  if (loading) {
    return (
      <DashboardLayout title="التحكم بتطبيقات الهاتف" titleEn="Mobile Apps Control" onBack={() => navigate('/dashboard?view=platform-control')} breadcrumb={breadcrumb}>
        <div className="flex items-center justify-center py-32 text-white/50 gap-2">
          <Loader2 className="w-5 h-5 animate-spin" /> {t('جاري التحميل…', 'Loading…')}
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="التحكم بتطبيقات الهاتف" titleEn="Mobile Apps Control" onBack={() => navigate('/dashboard?view=platform-control')} breadcrumb={breadcrumb}>
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6" data-testid="mobile-apps-control">

        {/* App picker */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {apps.map((a) => {
            const Icon = APP_ICON[a.id] || Smartphone;
            const active = a.id === activeApp;
            const blocked = (a.stats?.ios?.blocked_versions || 0) + (a.stats?.android?.blocked_versions || 0);
            return (
              <button
                key={a.id}
                onClick={() => setActiveApp(a.id)}
                className={`relative p-4 rounded-2xl border-2 text-start transition-all ${
                  active ? 'border-[#c9a227] bg-[#c9a227]/[0.08]' : 'border-white/10 bg-white/[0.02] hover:border-white/20'
                }`}
                data-testid={`app-card-${a.id}`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center"
                       style={{ backgroundColor: `${a.color}18`, border: `1px solid ${a.color}40` }}>
                    <Icon className="w-5 h-5" style={{ color: a.color }} />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-white font-bold text-sm">{isAr ? a.name_ar : a.name_en}</h3>
                    <p className="text-white/50 text-[11px]" dir="ltr">
                      iOS: {a.stats?.ios?.total_versions || 0} {t('إصدار', 'versions')} · Android: {a.stats?.android?.total_versions || 0} {t('إصدار', 'versions')}
                    </p>
                  </div>
                </div>
                {blocked > 0 && (
                  <div className="absolute top-2 end-2 px-2 py-0.5 rounded-full bg-red-500/15 border border-red-500/30 text-red-400 text-[10px] font-semibold">
                    {blocked} {t('محجوب', 'blocked')}
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {activeApp && activeAppMeta && (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* LEFT — versions + message + links */}
              <div className="space-y-4">
                {/* Platform tabs */}
                <div className="inline-flex rounded-xl bg-white/[0.03] border border-white/10 p-1 gap-1">
                  {[
                    { id: 'ios',     label: 'iOS',                      Icon: Apple },
                    { id: 'android', label: t('أندرويد', 'Android'),    Icon: Bot },
                  ].map((p) => {
                    const active = p.id === activePlat;
                    const nBlocked = activeAppMeta.stats?.[p.id]?.blocked_versions || 0;
                    return (
                      <button key={p.id} onClick={() => setActivePlat(p.id)}
                              className={`inline-flex items-center gap-2 px-4 h-10 rounded-lg text-sm font-semibold transition ${
                                active ? 'bg-white/10 text-white' : 'text-white/60 hover:text-white hover:bg-white/5'
                              }`}
                              data-testid={`platform-tab-${p.id}`}>
                        <p.Icon className="w-4 h-4" />
                        {p.label}
                        {nBlocked > 0 && <span className="px-1.5 rounded-full bg-red-500/20 text-red-300 text-[10px]">{nBlocked}</span>}
                      </button>
                    );
                  })}
                </div>

                {/* Versions list */}
                <div className="p-4 rounded-xl bg-white/[0.02] border border-white/10">
                  <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                    <div>
                      <label className="text-sm font-semibold text-white">
                        {t('الإصدارات — حجب / رفع الحجب لكل إصدار', 'Versions — block / unblock each one')}
                      </label>
                      <p className="text-[11px] text-white/45 mt-0.5">
                        {t(`${versions.length} إصدار · ${blockedCount} محجوب`, `${versions.length} versions · ${blockedCount} blocked`)}
                      </p>
                    </div>
                    <button onClick={loadVersions} disabled={refreshing}
                            className="text-white/60 hover:text-white p-1.5 rounded-md hover:bg-white/5"
                            data-testid="refresh-versions" title={t('تحديث القائمة', 'Refresh')}>
                      <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                    </button>
                  </div>

                  {versions.length === 0 ? (
                    <div className="p-6 rounded-lg border border-white/5 bg-white/[0.02] text-center">
                      <Smartphone className="w-8 h-8 text-white/20 mx-auto mb-2" />
                      <p className="text-white/60 text-sm">
                        {t(
                          `لا توجد إصدارات مسجّلة على ${activePlat} بعد — تظهر هنا تلقائياً عندما تسجّلها المنصة.`,
                          `No versions registered on ${activePlat} yet — they appear here once the platform registers them.`,
                        )}
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-1.5 max-h-[560px] overflow-y-auto pr-1" data-testid="versions-list">
                      {versions.map((v) => (
                        <VersionRow key={rowKey(v)} v={v} busy={busyVersion === rowKey(v)} onToggle={toggleBlock} isAr={isAr} t={t} />
                      ))}
                    </div>
                  )}
                </div>

                {/* Message editor */}
                <div className="p-4 rounded-xl bg-white/[0.02] border border-white/10 space-y-3">
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <h4 className="text-sm font-semibold text-white">
                        {t('رسالة التحديث العامة (عربي + إنجليزي)', 'General update message (AR + EN)')}
                      </h4>
                      <p className="text-[11px] text-white/45 mt-0.5">
                        {t('تظهر لأي إصدار محجوب من هذا التطبيق — بالعربي للتطبيق العربي وبالإنجليزي للتطبيق الإنجليزي.',
                           'Shown for every blocked version of this app — Arabic for Arabic apps, English for English apps.')}
                      </p>
                    </div>
                    <button onClick={() => setCopy(DEFAULT_COPY)}
                            className="text-[11px] text-white/60 hover:text-white px-2 py-1 rounded hover:bg-white/5 shrink-0"
                            data-testid="reset-defaults">
                      {t('النص الافتراضي', 'Defaults')}
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <div className="text-[10px] uppercase text-white/50 font-semibold">AR</div>
                      <Input value={copy.title_ar}  onChange={(e) => setCopy({ ...copy, title_ar: e.target.value })}  placeholder="عنوان بالعربي" data-testid="input-title-ar" dir="rtl" />
                      <Textarea rows={4} value={copy.body_ar} onChange={(e) => setCopy({ ...copy, body_ar: e.target.value })} placeholder="نص بالعربي" data-testid="input-body-ar" dir="rtl" />
                      <Input value={copy.button_ar} onChange={(e) => setCopy({ ...copy, button_ar: e.target.value })} placeholder="نص الزر" data-testid="input-button-ar" dir="rtl" />
                    </div>
                    <div className="space-y-2">
                      <div className="text-[10px] uppercase text-white/50 font-semibold">EN</div>
                      <Input value={copy.title_en}  onChange={(e) => setCopy({ ...copy, title_en: e.target.value })}  placeholder="Title in English" data-testid="input-title-en" dir="ltr" />
                      <Textarea rows={4} value={copy.body_en} onChange={(e) => setCopy({ ...copy, body_en: e.target.value })} placeholder="Body in English" data-testid="input-body-en" dir="ltr" />
                      <Input value={copy.button_en} onChange={(e) => setCopy({ ...copy, button_en: e.target.value })} placeholder="Button text" data-testid="input-button-en" dir="ltr" />
                    </div>
                  </div>
                  <Button onClick={saveMessage} disabled={savingMsg}
                          className="w-full bg-[#c9a227] hover:bg-[#b8922a] text-black font-semibold"
                          data-testid="save-message-btn">
                    {savingMsg ? <Loader2 className="w-4 h-4 me-2 animate-spin" /> : <Save className="w-4 h-4 me-2" />}
                    {t('حفظ رسالة التحديث', 'Save update message')}
                  </Button>
                </div>

                {/* Store links */}
                <div className="p-4 rounded-xl bg-white/[0.02] border border-white/10 space-y-3">
                  <div>
                    <h4 className="text-sm font-semibold text-white">{t('روابط المتجر', 'Store links')}</h4>
                    <p className="text-[11px] text-white/45 mt-0.5">
                      {t('التطبيق على iOS يستلم رابط App Store، وعلى أندرويد رابط Google Play تلقائياً.',
                         'iOS apps receive the App Store link, Android apps receive the Google Play link automatically.')}
                    </p>
                  </div>
                  <div className="space-y-2">
                    <div className={`flex items-center gap-2 rounded-lg p-1 ${activePlat === 'ios' ? 'bg-white/[0.04]' : ''}`}>
                      <Apple className={`w-4 h-4 shrink-0 ${activePlat === 'ios' ? 'text-[#c9a227]' : 'text-white/50'}`} />
                      <Input value={storeIos} onChange={(e) => setStoreIos(e.target.value)} placeholder="https://apps.apple.com/..." dir="ltr" data-testid="input-store-ios" />
                    </div>
                    <div className={`flex items-center gap-2 rounded-lg p-1 ${activePlat === 'android' ? 'bg-white/[0.04]' : ''}`}>
                      <Bot className={`w-4 h-4 shrink-0 ${activePlat === 'android' ? 'text-[#c9a227]' : 'text-white/50'}`} />
                      <Input value={storeAndroid} onChange={(e) => setStoreAndroid(e.target.value)} placeholder="https://play.google.com/..." dir="ltr" data-testid="input-store-android" />
                    </div>
                  </div>
                  <Button onClick={saveLinks} disabled={savingLinks} variant="outline"
                          className="w-full border-white/15 text-white hover:bg-white/5"
                          data-testid="save-links-btn">
                    {savingLinks ? <Loader2 className="w-4 h-4 me-2 animate-spin" /> : <Save className="w-4 h-4 me-2" />}
                    {t('حفظ روابط المتجر', 'Save store links')}
                  </Button>
                </div>
              </div>

              {/* RIGHT — phone preview */}
              <PreviewCard
                copy={copy}
                storeUrl={activePlat === 'ios' ? storeIos : storeAndroid}
                previewLocale={previewLocale}
                setPreviewLocale={setPreviewLocale}
                isAr={isAr}
                platform={activePlat}
                blockedCount={blockedCount}
              />
            </div>

            <AuditTable audit={audit} isAr={isAr} />
          </>
        )}
      </section>
    </DashboardLayout>
  );
}

function VersionRow({ v, busy, onToggle, isAr, t }) {
  const src = SOURCE_META[v.source] || SOURCE_META.platform;
  const testSuffix = v.build_number ? `${v.version}-${v.build_number}` : v.version;
  return (
    <div
      className={`flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg border transition ${
        v.blocked ? 'border-red-500/40 bg-red-500/[0.08]' : 'border-white/5 bg-white/[0.02]'
      }`}
      data-testid={`version-row-${testSuffix}`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <span className={`w-2 h-2 rounded-full shrink-0 ${v.blocked ? 'bg-red-400' : 'bg-emerald-400'}`} />
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`font-mono text-sm font-semibold tabular-nums ${v.blocked ? 'text-red-300 line-through' : 'text-white'}`} dir="ltr">
              Version {v.version}
            </span>
            {v.install_count > 0 && (
              <span className="px-1.5 py-0.5 rounded-full bg-white/5 text-[10px] text-white/60">
                {v.install_count} {t('جهاز', 'installs')}
              </span>
            )}
            {v.source !== 'platform' && (
              <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] ${src.cls}`}>
                <src.Icon className="w-3 h-3" /> {isAr ? src.ar : src.en}
              </span>
            )}
          </div>
          <div className="text-[11px] text-white/45 font-mono mt-0.5 flex items-center gap-1.5 flex-wrap" dir="ltr"
               data-testid={`version-meta-${testSuffix}`}>
            <span title={v.released_at || ''}>{buildDateTime(v.released_at || v.registered_at)}</span>
            <span className="text-white/25">•</span>
            <span className="text-white/60">{v.build_number || t('بدون كود', 'no code')}</span>
            {!v.released_at && <span className="text-amber-300/70">({t('تاريخ التسجيل', 'registered')})</span>}
          </div>
        </div>
      </div>
      <button
        onClick={() => onToggle(v)}
        disabled={busy}
        className={`h-8 px-3 rounded-lg text-xs font-semibold inline-flex items-center gap-1.5 transition disabled:opacity-50 shrink-0 ${
          v.blocked
            ? 'bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300 border border-emerald-500/30'
            : 'bg-red-500/15 hover:bg-red-500/25 text-red-300 border border-red-500/30'
        }`}
        data-testid={`toggle-block-${testSuffix}`}
      >
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
              : v.blocked ? <ShieldCheck className="w-3.5 h-3.5" /> : <Ban className="w-3.5 h-3.5" />}
        {v.blocked ? t('رفع الحجب', 'Unblock') : t('حجب', 'Block')}
      </button>
    </div>
  );
}

function PreviewCard({ copy, storeUrl, previewLocale, setPreviewLocale, isAr, platform, blockedCount }) {
  const isEn = previewLocale === 'en';
  const t = (a, e) => (isAr ? a : e);
  const title  = isEn ? copy.title_en  : copy.title_ar;
  const body   = isEn ? copy.body_en   : copy.body_ar;
  const button = isEn ? copy.button_en : copy.button_ar;

  return (
    <div className="p-4 rounded-xl bg-white/[0.02] border border-white/10 space-y-3 h-fit lg:sticky lg:top-24">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-white">{t('معاينة مباشرة على الهاتف', 'Live phone preview')}</h4>
        <div className="inline-flex rounded-lg bg-white/[0.03] border border-white/10 p-0.5">
          {['ar', 'en'].map((lang) => (
            <button key={lang} onClick={() => setPreviewLocale(lang)}
                    className={`px-3 h-7 text-xs font-semibold rounded-md ${previewLocale === lang ? 'bg-white/10 text-white' : 'text-white/50 hover:text-white'}`}
                    data-testid={`preview-locale-${lang}`}>
              {lang.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      <div className="mx-auto max-w-[280px] aspect-[9/16] rounded-[36px] border-[6px] border-white/10 bg-black relative overflow-hidden shadow-2xl">
        <div className="absolute top-2 left-1/2 -translate-x-1/2 w-16 h-4 bg-white/10 rounded-full" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0a0a0a] via-[#101020] to-[#0a0a0a]" />
        <div className="absolute inset-0 flex items-center justify-center p-4">
          <div dir={isEn ? 'ltr' : 'rtl'}
               className="w-full aspect-square bg-[#111121] rounded-2xl border border-[#c9a227]/30 p-4 flex flex-col justify-between shadow-[0_0_40px_rgba(201,162,39,0.15)]"
               data-testid="preview-gate-card">
            <div className="text-center">
              <div className="text-2xl mb-1.5">{title.split(' ')[0] || '✨'}</div>
              <div className="text-white font-bold text-[13px] leading-tight mb-2 whitespace-pre-wrap">{title.replace(/^✨\s*/, '')}</div>
              <div className="text-white/70 text-[10px] leading-relaxed whitespace-pre-wrap max-h-[110px] overflow-hidden">{body}</div>
            </div>
            <div className="mt-3">
              <div className="w-full py-2 rounded-lg bg-[#c9a227] text-black text-center text-[12px] font-bold">{button}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="text-[11px] text-white/50 text-center">
        {blockedCount > 0
          ? t(`تظهر هذه البطاقة الآن لـ ${blockedCount} إصدار محجوب على ${platform}`, `Shown right now to ${blockedCount} blocked version(s) on ${platform}`)
          : t(`لا يوجد إصدار محجوب على ${platform} حالياً`, `No blocked versions on ${platform} right now`)}
      </div>

      {storeUrl ? (
        <a href={storeUrl} target="_blank" rel="noopener noreferrer"
           className="text-[11px] text-[#c9a227] hover:underline inline-flex items-center gap-1" dir="ltr">
          {platform === 'ios' ? 'App Store' : 'Google Play'} <ExternalLink className="w-3 h-3" />
        </a>
      ) : (
        <div className="text-[11px] text-amber-300/70 inline-flex items-center gap-1">
          <AlertTriangle className="w-3 h-3" />
          {t(`أضف رابط متجر ${platform === 'ios' ? 'App Store' : 'Google Play'} ليعمل زر التحديث`, `Add the ${platform === 'ios' ? 'App Store' : 'Google Play'} link so the Update button works`)}
        </div>
      )}
    </div>
  );
}

function AuditTable({ audit, isAr }) {
  const t = (a, e) => (isAr ? a : e);
  if (!audit?.length) {
    return (
      <div className="mt-6 p-4 rounded-xl bg-white/[0.02] border border-white/10 text-center text-white/50 text-sm">
        {t('لا يوجد سجل عمليات بعد', 'No audit entries yet')}
      </div>
    );
  }
  return (
    <div className="mt-6" data-testid="audit-table">
      <h3 className="text-sm font-bold text-white mb-2">{t('سجل العمليات (الأحدث أولاً)', 'Audit Log (newest first)')}</h3>
      <div className="rounded-xl border border-white/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-white/[0.03] text-white/60 text-xs">
            <tr>
              <th className="text-start px-3 py-2">{t('التاريخ', 'When')}</th>
              <th className="text-start px-3 py-2">{t('الإجراء', 'Action')}</th>
              <th className="text-start px-3 py-2">{t('المنصة', 'Platform')}</th>
              <th className="text-start px-3 py-2">{t('الإصدار', 'Version')}</th>
              <th className="text-start px-3 py-2">{t('الأثر', 'Impact')}</th>
              <th className="text-start px-3 py-2">{t('المسؤول', 'By')}</th>
            </tr>
          </thead>
          <tbody>
            {audit.map((r) => {
              const meta = ACTION_LABEL[r.action] || { ar: r.action, en: r.action, cls: 'bg-white/5 text-white/60' };
              const ver = r.version || r.cutoff_version;
              return (
                <tr key={r.id} className="border-t border-white/5">
                  <td className="px-3 py-2 text-white/60 text-xs whitespace-nowrap" dir="ltr">{(r.created_at || '').slice(0, 19).replace('T', ' ')}</td>
                  <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${meta.cls}`}>{isAr ? meta.ar : meta.en}</span></td>
                  <td className="px-3 py-2 text-white/70 text-xs">{r.platform || '—'}</td>
                  <td className="px-3 py-2 font-mono text-white/80 text-xs" dir="ltr">
                    {ver ? `v${ver}` : '—'}{r.build_number ? <span className="text-white/40"> ({r.build_number})</span> : null}
                  </td>
                  <td className="px-3 py-2 text-white/70 text-xs">{r.installs_affected != null ? `${r.installs_affected} ${t('جهاز', 'installs')}` : '—'}</td>
                  <td className="px-3 py-2 text-white/60 text-xs" dir="ltr">{r.actor_email || '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
